import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { captureException } from '@sentry/react';
import { useAuth } from 'app/shared-components/auth/AuthProvider';
import {
  FeatureFlagsState,
  getPublicFlags,
  getRemoteFlags,
  initialState,
  selectFeatureFlags,
  setFlagsLoaded,
} from 'app/store/featureFlagsSlice';

const FeatureFlagContext = createContext<FeatureFlagsState>(initialState);

interface FeatureFlagProviderProps {
  children: ReactNode;
  onFeatureFlagsLoaded?: () => void;
}

function FeatureFlagProvider({ children, onFeatureFlagsLoaded }: FeatureFlagProviderProps): JSX.Element {
  const dispatch = useAppDispatch();
  const featureFlagsState = useAppSelector(selectFeatureFlags);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { user } = useAuth();
  const { areFeatureFlagsLoaded } = featureFlagsState;

  useEffect(() => {
    // Only fetch feature flags when we have both user AND a valid paidol ID
    // This prevents the race condition where flags are fetched with empty paidol,
    // causing features like credit management to not appear even when they should
    if (user && selectedPaidol?.id) {
      dispatch(
        getRemoteFlags({
          email: user.email,
          paidolId: selectedPaidol.id,
        })
      ).finally(() => {
        dispatch(setFlagsLoaded(true));
        if (onFeatureFlagsLoaded) {
          onFeatureFlagsLoaded();
        }
      });
    } else {
      // Pre-paidol contexts (registration, invitation-code onboarding) can't call
      // listFeatureFlags (it requires email + paidolId). Load the whitelisted
      // public-flag subset via IAM auth so onboarding-time kill switches like
      // `enableAddressValidation` can still take effect (SEE-538).
      dispatch(getPublicFlags()).finally(() => {
        dispatch(setFlagsLoaded(true));
        if (onFeatureFlagsLoaded) {
          onFeatureFlagsLoaded();
        }
      });
    }
  }, [dispatch, onFeatureFlagsLoaded, selectedPaidol?.id, user]);

  useEffect(() => {
    const refreshFlagsInterval = setInterval(() => {
      if (areFeatureFlagsLoaded) {
        // Only refresh if we have both user and valid paidol ID
        if (user && selectedPaidol?.id) {
          dispatch(
            getRemoteFlags({
              email: user.email,
              paidolId: selectedPaidol.id,
            })
          );
        }
      }
    }, 15 * 60 * 1000);

    return () => clearInterval(refreshFlagsInterval);
  }, [areFeatureFlagsLoaded, dispatch, selectedPaidol?.id, user]);

  // Render children immediately — flags load in the background.
  // In practice, RequireAuth gates rendering until areFeatureFlagsLoaded is true for
  // authenticated routes, so consumers never observe the undefined (not-yet-loaded) state.
  return <FeatureFlagContext.Provider value={featureFlagsState}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsState {
  return useContext(FeatureFlagContext);
}

export function isEnabled(
  featureName: string,
  flagName: string,
  flagsState?: FeatureFlagsState
): boolean | undefined {
  if (flagsState === undefined) return;
  const { areFeatureFlagsLoaded, flags } = flagsState;

  if (!areFeatureFlagsLoaded) {
    return undefined;
  }

  let flagValue = flags[featureName];

  if (flagValue === undefined) {
    return false;
  }

  if (typeof flagValue === 'string') {
    try {
      flagValue = JSON.parse(flagValue);
    } catch (e) {
      captureException(e);
      console.error(`Error parsing feature flag ${featureName}: ${e}`);
      return flagValue.toLowerCase() !== 'false';
    }
  }

  if (flagValue && typeof flagValue === 'object') {
    const rawFlag = flagValue[flagName];
    if (rawFlag === undefined) {
      return false;
    }
    if (typeof rawFlag === 'boolean') {
      return rawFlag;
    }
    if (typeof rawFlag === 'string') {
      return rawFlag.toLowerCase() !== 'false';
    }

    return Boolean(rawFlag);
  }

  return flagValue;
}

export const useIsEnabled = (
  featureFlags: { featureName: string; flagName: string }[]
): Record<string, boolean | undefined> => {
  const flagsState = useFeatureFlags();

  // SEE-946: read the flag state directly from context — the previous
  // implementation mirrored it into local component state via useState +
  // useEffect, which introduced a one-render window where the local mirror
  // was still `undefined` while the context already had real values. Every
  // consumer of useIsEnabled (including useUserRole) therefore saw
  // `flagValue === undefined` for one frame after any remount/navigation.
  // For Approval/Secondary admin paidols this collapsed the flag-loaded
  // check to `additionalAdminRolesEnabled = false`, which triggered the
  // fail-open path and briefly rendered the user as a Primary Admin
  // (visible as an Insights tab flash in the nav menu on cross-route
  // navigation). Using the context value directly fixes the flash and
  // costs nothing: useFeatureFlags already returns a stable reference when
  // the context hasn't changed.
  const result = featureFlags.reduce((acc, { featureName, flagName }) => {
    acc[`${featureName}.${flagName}`] = isEnabled(featureName, flagName, flagsState);
    return acc;
  }, {} as Record<string, boolean | undefined>);

  return result;
};

export default FeatureFlagProvider;
